import {
  BOARD_HEIGHT,
  BOARD_WIDTH,
  type Board,
  type CellGravityAnimation,
  type Cell,
  type EraseAnimation,
  type EraseTextAnimation,
  type FallingPair,
  type GameOverAnimation,
  type LandingAnimation,
  type PairAnimation,
  type PieceType,
  pairCells
} from "../core/model";
import { hashSeed } from "../core/random";
import { canPlacePair } from "../core/rules";
import { drawEraseEffect } from "./effects";
import { PIECE_STYLES, drawPiece } from "./spriteAtlas";
import { staticDitherIndex } from "./staticTexture";

export interface BoardRenderState {
  board: Board;
  currentPair: FallingPair | null;
  pairAnimation: PairAnimation | null;
  gravityAnimation: CellGravityAnimation | null;
  landingAnimation: LandingAnimation | null;
  eraseAnimation: EraseAnimation | null;
  eraseTextAnimations: EraseTextAnimation[];
  gameOverAnimation: GameOverAnimation | null;
  highContrast: boolean;
  time: number;
  text: BoardRenderText;
}

export interface BoardRenderText {
  gameOver: string;
  chain: (chain: number) => string;
  colorCombo: (colorCount: number) => string;
}

export interface BoardMetrics {
  cell: number;
  boardX: number;
  boardY: number;
  boardWidth: number;
  boardHeight: number;
}

export const BOARD_FRAME_FACTOR = 0.22;
export const BOARD_FRAMED_WIDTH = BOARD_WIDTH + BOARD_FRAME_FACTOR * 2;
export const BOARD_FRAMED_HEIGHT = BOARD_HEIGHT + BOARD_FRAME_FACTOR * 2;
export const BOARD_FRAMED_ASPECT = BOARD_FRAMED_WIDTH / BOARD_FRAMED_HEIGHT;
const DITHER_CACHE_LIMIT = 12;
const ditherCache = new Map<string, HTMLCanvasElement>();

function configureCanvas(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const rect = canvas.getBoundingClientRect();
  const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
  const width = Math.max(1, Math.floor(rect.width * dpr));
  const height = Math.max(1, Math.floor(rect.height * dpr));
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas 2D context is unavailable");
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

function metricsFor(canvas: HTMLCanvasElement): BoardMetrics {
  const rect = canvas.getBoundingClientRect();
  const cell = Math.max(1, Math.min(rect.width / BOARD_FRAMED_WIDTH, rect.height / BOARD_FRAMED_HEIGHT));
  const frame = cell * BOARD_FRAME_FACTOR;
  const boardWidth = cell * BOARD_WIDTH;
  const boardHeight = cell * BOARD_HEIGHT;
  return {
    cell,
    boardX: frame,
    boardY: frame,
    boardWidth,
    boardHeight
  };
}

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number): void {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function drawDither(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  palette: string[],
  step: number,
  alpha = 1
): void {
  const safeStep = Math.max(1, Math.floor(step));
  if (safeStep === 1) {
    const widthPx = Math.max(1, Math.ceil(width));
    const heightPx = Math.max(1, Math.ceil(height));
    const originX = Math.floor(x);
    const originY = Math.floor(y);
    const cacheKey = `${widthPx}x${heightPx}:${originX},${originY}:${palette.join(",")}`;
    let texture = ditherCache.get(cacheKey);
    if (!texture) {
      texture = makeDitherTexture(widthPx, heightPx, originX, originY, palette);
      ditherCache.set(cacheKey, texture);
      if (ditherCache.size > DITHER_CACHE_LIMIT) {
        const oldestKey = ditherCache.keys().next().value;
        if (oldestKey) {
          ditherCache.delete(oldestKey);
        }
      }
    }
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(texture, x, y);
    ctx.restore();
    return;
  }

  ctx.save();
  ctx.globalAlpha = alpha;
  for (let py = y; py < y + height; py += safeStep) {
    for (let px = x; px < x + width; px += safeStep) {
      ctx.fillStyle = palette[staticDitherIndex(px, py, palette.length)];
      ctx.fillRect(px, py, safeStep, safeStep);
    }
  }
  ctx.restore();
}

function makeDitherTexture(width: number, height: number, originX: number, originY: number, palette: string[]): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return canvas;
  }

  const colors = palette.map(hexToRgb);
  const image = ctx.createImageData(width, height);
  for (let py = 0; py < height; py += 1) {
    for (let px = 0; px < width; px += 1) {
      const color = colors[staticDitherIndex(originX + px, originY + py, colors.length)];
      const offset = (py * width + px) * 4;
      image.data[offset] = color[0];
      image.data[offset + 1] = color[1];
      image.data[offset + 2] = color[2];
      image.data[offset + 3] = 255;
    }
  }
  ctx.putImageData(image, 0, 0);
  return canvas;
}

function hexToRgb(color: string): [number, number, number] {
  const value = color.startsWith("#") ? color.slice(1) : color;
  const normalized = value.length === 3 ? value.split("").map((part) => part + part).join("") : value;
  const parsed = Number.parseInt(normalized, 16);
  return [(parsed >> 16) & 255, (parsed >> 8) & 255, parsed & 255];
}

function drawBoardShell(ctx: CanvasRenderingContext2D, metrics: BoardMetrics): void {
  const { boardX, boardY, boardWidth, boardHeight, cell } = metrics;
  const frame = cell * BOARD_FRAME_FACTOR;
  const gradient = ctx.createLinearGradient(boardX, boardY, boardX + boardWidth, boardY + boardHeight);
  gradient.addColorStop(0, "#1bd4ff");
  gradient.addColorStop(0.38, "#087ba3");
  gradient.addColorStop(0.72, "#123243");
  gradient.addColorStop(1, "#30414a");
  ctx.fillStyle = gradient;
  ctx.fillRect(boardX - frame, boardY - frame, boardWidth + frame * 2, boardHeight + frame * 2);
  drawDither(
    ctx,
    boardX - frame,
    boardY - frame,
    boardWidth + frame * 2,
    boardHeight + frame * 2,
    ["#003b6b", "#0064ad", "#0ad4ff", "#09243a", "#5d6b25"],
    1,
    0.58
  );

  ctx.fillStyle = "#05331d";
  ctx.fillRect(boardX, boardY, boardWidth, boardHeight);

  for (let y = 0; y < BOARD_HEIGHT; y += 1) {
    for (let x = 0; x < BOARD_WIDTH; x += 1) {
      const gap = Math.max(2, Math.floor(cell * 0.055));
      const cellX = boardX + x * cell + gap;
      const cellY = boardY + y * cell + gap;
      roundedRect(ctx, cellX, cellY, cell - gap * 2, cell - gap * 2, Math.max(4, cell * 0.08));
      const cellGradient = ctx.createLinearGradient(cellX, cellY, cellX + cell, cellY + cell);
      cellGradient.addColorStop(0, "#3a1210");
      cellGradient.addColorStop(0.52, "#260c0a");
      cellGradient.addColorStop(1, "#110604");
      ctx.fillStyle = cellGradient;
      ctx.fill();
      ctx.strokeStyle = "rgba(0,0,0,0.42)";
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }

  ctx.strokeStyle = "rgba(255,255,255,0.68)";
  ctx.lineWidth = Math.max(2, cell * 0.035);
  ctx.strokeRect(boardX - frame + 0.5, boardY - frame + 0.5, boardWidth + frame * 2 - 1, boardHeight + frame * 2 - 1);
}

function isMarked(erase: EraseAnimation | null, x: number, y: number): boolean {
  return Boolean(erase?.marked[y]?.[x]);
}

function drawCell(
  ctx: CanvasRenderingContext2D,
  cell: Cell,
  boardX: number,
  boardY: number,
  gridX: number,
  gridY: number,
  cellSize: number,
  erase: EraseAnimation | null,
  highContrast: boolean,
  phase: number,
  active = false,
  ghost = false,
  squash = 0
): void {
  if (cell === 0) {
    return;
  }
  const drawX = boardX + gridX * cellSize;
  const drawY = boardY + gridY * cellSize;
  const inset = Math.max(2, cellSize * 0.07);

  if (isMarked(erase, gridX, gridY)) {
    drawEraseEffect(
      ctx,
      cell as PieceType,
      drawX + inset,
      drawY + inset,
      cellSize - inset * 2,
      erase?.frame ?? 0,
      hashSeed(erase?.seed ?? 0, gridX, gridY, cell),
      highContrast
    );
  } else {
    drawPiece(ctx, cell as PieceType, drawX + inset, drawY + inset, cellSize - inset * 2, {
      highContrast,
      phase,
      active,
      ghost,
      squash
    });
  }
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function easeOutCubic(value: number): number {
  const t = clamp01(value);
  return 1 - (1 - t) ** 3;
}

function easeInOutCubic(value: number): number {
  const t = clamp01(value);
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

function floatingTextAlpha(progress: number): number {
  const t = clamp01(progress);
  if (t < 0.12) {
    return easeOutCubic(t / 0.12);
  }
  if (t < 0.58) {
    return 1;
  }
  return 1 - easeInOutCubic((t - 0.58) / 0.42);
}

function landingSquash(value: number): number {
  const t = Math.max(0, Math.min(1, value));
  if (t < 0.34) {
    return Math.sin((t / 0.34) * Math.PI) * 1.25;
  }
  const rebound = (t - 0.34) / 0.66;
  return -Math.sin(rebound * Math.PI) * 0.34 * (1 - rebound);
}

function isGravityTarget(animation: CellGravityAnimation | null, x: number, y: number): boolean {
  return Boolean(animation?.moves.some((move) => move.toX === x && move.toY === y));
}

function landingSquashFor(animation: LandingAnimation | null, x: number, y: number): number {
  if (!animation || !animation.cells.some((cell) => cell.x === x && cell.y === y)) {
    return 0;
  }
  return landingSquash(animation.elapsed / animation.duration) * 1.15;
}

function gameOverSquashFor(animation: GameOverAnimation | null, x: number, y: number): number {
  if (!animation) {
    return 0;
  }
  const stagger = (BOARD_HEIGHT - 1 - y) * 0.018 + x * 0.006;
  const t = clamp01(animation.elapsed / animation.duration - stagger);
  if (t <= 0) {
    return 0;
  }
  if (t < 0.38) {
    return Math.sin((t / 0.38) * Math.PI) * 1.42;
  }
  if (t < 0.72) {
    const rebound = (t - 0.38) / 0.34;
    return -Math.sin(rebound * Math.PI) * 0.28 * (1 - rebound * 0.35);
  }
  return 0;
}

function drawAnimatedPair(
  ctx: CanvasRenderingContext2D,
  metrics: BoardMetrics,
  state: BoardRenderState
): void {
  if (!state.currentPair) {
    return;
  }

  const animation = state.pairAnimation;
  const toCells = pairCells(state.currentPair);
  const fromCells = animation ? pairCells(animation.from) : toCells;
  const progress = animation ? easeOutCubic(animation.elapsed / animation.duration) : 1;
  const squash = animation && progress > 0.78 ? landingSquash((progress - 0.78) / 0.22) * 0.95 : 0;

  for (let index = 0; index < toCells.length; index += 1) {
    const from = fromCells[index];
    const to = toCells[index];
    const gridX = from.x + (to.x - from.x) * progress;
    const gridY = from.y + (to.y - from.y) * progress;
    if (gridY < 0) {
      continue;
    }
    drawCell(
      ctx,
      to.type,
      metrics.boardX,
      metrics.boardY,
      gridX,
      gridY,
      metrics.cell,
      null,
      state.highContrast,
      state.time + index * 90,
      true,
      false,
      squash
    );
  }
}

function drawGravityAnimation(ctx: CanvasRenderingContext2D, metrics: BoardMetrics, state: BoardRenderState): void {
  const animation = state.gravityAnimation;
  if (!animation) {
    return;
  }

  const progress = easeOutCubic(animation.elapsed / animation.duration);
  const squash = progress > 0.78 ? landingSquash((progress - 0.78) / 0.22) * 1.15 : 0;
  for (const move of animation.moves) {
    const gridY = move.fromY + (move.toY - move.fromY) * progress;
    drawCell(
      ctx,
      move.type,
      metrics.boardX,
      metrics.boardY,
      move.fromX,
      gridY,
      metrics.cell,
      null,
      state.highContrast,
      state.time + move.toX * 137 + move.toY * 89,
      true,
      false,
      squash
    );
  }
}

function drawEraseTextBursts(ctx: CanvasRenderingContext2D, metrics: BoardMetrics, animations: EraseTextAnimation[], text: BoardRenderText): void {
  if (animations.length === 0) {
    return;
  }

  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineJoin = "round";

  for (const animation of animations) {
    const rawProgress = clamp01(animation.elapsed / animation.duration);
    const progress = easeOutCubic(Math.min(1, rawProgress * 1.08));
    const alpha = floatingTextAlpha(rawProgress);
    if (alpha <= 0.01) {
      continue;
    }

    for (const burst of animation.bursts) {
      const style = PIECE_STYLES[burst.type];
      const distance = burst.distance * metrics.cell * (0.22 + progress * 0.86);
      const x = metrics.boardX + burst.x * metrics.cell + Math.cos(burst.angle) * distance;
      const y = metrics.boardY + burst.y * metrics.cell + Math.sin(burst.angle) * distance - metrics.cell * 0.3 * progress;
      ctx.globalAlpha = alpha;
      ctx.font = `1000 ${Math.max(18, Math.floor(metrics.cell * 0.56))}px Arial Black, Segoe UI Black, system-ui, sans-serif`;
      ctx.strokeStyle = "rgba(0,0,0,0.84)";
      ctx.lineWidth = Math.max(4, metrics.cell * 0.11);
      ctx.shadowColor = style.glow;
      ctx.shadowBlur = metrics.cell * (0.18 + 0.1 * alpha);
      ctx.strokeText(String(burst.count), x, y);
      ctx.fillStyle = style.base;
      ctx.fillText(String(burst.count), x, y);
      ctx.fillStyle = style.light;
      ctx.globalAlpha = alpha * 0.78;
      ctx.fillText(String(burst.count), x - metrics.cell * 0.04, y - metrics.cell * 0.05);
    }

    if (animation.colorBurst) {
      const colorText = text.colorCombo(animation.colorBurst.colorCount);
      const drift = metrics.cell * 0.34 * progress;
      const x = metrics.boardX + animation.colorBurst.x * metrics.cell + Math.cos(animation.colorBurst.angle) * drift;
      const y = metrics.boardY + animation.colorBurst.y * metrics.cell - metrics.cell * (1.42 + progress * 1.25);
      ctx.globalAlpha = alpha;
      ctx.font = `1000 ${Math.max(17, Math.floor(metrics.cell * 0.44))}px Arial Black, Segoe UI Black, system-ui, sans-serif`;
      ctx.strokeStyle = "rgba(0,0,0,0.9)";
      ctx.lineWidth = Math.max(4, metrics.cell * 0.1);
      ctx.shadowColor = "rgba(0,216,255,0.74)";
      ctx.shadowBlur = metrics.cell * 0.32;
      ctx.strokeText(colorText, x, y);
      ctx.fillStyle = "#7ff2ff";
      ctx.fillText(colorText, x, y);
      ctx.globalAlpha = alpha * 0.82;
      ctx.fillStyle = "#fffbd2";
      ctx.fillText(colorText, x - metrics.cell * 0.04, y - metrics.cell * 0.05);
    }

    if (animation.chainBurst) {
      const x = metrics.boardX + animation.chainBurst.x * metrics.cell;
      const y = metrics.boardY + animation.chainBurst.y * metrics.cell - metrics.cell * (0.55 + progress * 1.28);
      ctx.globalAlpha = alpha;
      ctx.font = `1000 ${Math.max(16, Math.floor(metrics.cell * 0.42))}px Arial Black, Segoe UI Black, system-ui, sans-serif`;
      ctx.strokeStyle = "rgba(0,0,0,0.88)";
      ctx.lineWidth = Math.max(4, metrics.cell * 0.09);
      ctx.shadowColor = "rgba(244,204,79,0.72)";
      ctx.shadowBlur = metrics.cell * 0.28;
      const chainText = text.chain(animation.chainBurst.chain);
      ctx.strokeText(chainText, x, y);
      ctx.fillStyle = "#fff7b6";
      ctx.fillText(chainText, x, y);
    }
  }

  ctx.restore();
}

function drawGameOverOverlay(ctx: CanvasRenderingContext2D, metrics: BoardMetrics, animation: GameOverAnimation | null, text: string): void {
  if (!animation) {
    return;
  }

  const rawProgress = clamp01(animation.elapsed / animation.duration);
  const rise = easeOutCubic(rawProgress);
  const alpha = rawProgress < 0.16 ? easeOutCubic(rawProgress / 0.16) : 1;
  const x = metrics.boardX + metrics.boardWidth / 2;
  const y = metrics.boardY + metrics.boardHeight * 0.54 - metrics.cell * (0.55 + rise * 1.35);
  const size = Math.max(20, Math.floor(metrics.cell * 0.54));

  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineJoin = "round";
  ctx.globalAlpha = alpha;
  ctx.font = `1000 ${size}px Arial Black, Segoe UI Black, system-ui, sans-serif`;
  ctx.shadowColor = "rgba(255, 54, 25, 0.78)";
  ctx.shadowBlur = metrics.cell * 0.38;
  ctx.strokeStyle = "rgba(0,0,0,0.92)";
  ctx.lineWidth = Math.max(5, metrics.cell * 0.12);
  ctx.strokeText(text, x, y);

  const gradient = ctx.createLinearGradient(x, y - size * 0.7, x, y + size * 0.8);
  gradient.addColorStop(0, "#fff4a5");
  gradient.addColorStop(0.48, "#ff5a2e");
  gradient.addColorStop(1, "#a80f18");
  ctx.fillStyle = gradient;
  ctx.fillText(text, x, y);

  ctx.globalAlpha = alpha * 0.58;
  ctx.fillStyle = "#ffffff";
  ctx.fillText(text, x - metrics.cell * 0.04, y - metrics.cell * 0.07);
  ctx.restore();
}

function landingPair(board: Board, pair: FallingPair): FallingPair {
  let ghost = { ...pair };
  while (canPlacePair(board, { ...ghost, y: ghost.y + 1 })) {
    ghost = { ...ghost, y: ghost.y + 1 };
  }
  return ghost;
}

export function renderBoard(canvas: HTMLCanvasElement, state: BoardRenderState): BoardMetrics {
  const ctx = configureCanvas(canvas);
  const rect = canvas.getBoundingClientRect();
  ctx.clearRect(0, 0, rect.width, rect.height);

  const metrics = metricsFor(canvas);
  drawBoardShell(ctx, metrics);

  if (state.currentPair) {
    const ghost = landingPair(state.board, state.currentPair);
    if (ghost.y !== state.currentPair.y) {
      for (const cell of pairCells(ghost)) {
        if (cell.y < 0) {
          continue;
        }
        drawCell(
          ctx,
          cell.type,
          metrics.boardX,
          metrics.boardY,
          cell.x,
          cell.y,
          metrics.cell,
          null,
          state.highContrast,
          state.time,
          false,
          true
        );
      }
    }
  }

  for (let y = 0; y < BOARD_HEIGHT; y += 1) {
    for (let x = 0; x < BOARD_WIDTH; x += 1) {
      if (isGravityTarget(state.gravityAnimation, x, y)) {
        continue;
      }
      drawCell(
        ctx,
        state.board[y][x],
        metrics.boardX,
        metrics.boardY,
        x,
        y,
        metrics.cell,
        state.eraseAnimation,
        state.highContrast,
        state.time + x * 137 + y * 89,
        false,
        false,
        landingSquashFor(state.landingAnimation, x, y) + gameOverSquashFor(state.gameOverAnimation, x, y)
      );
    }
  }

  drawGravityAnimation(ctx, metrics, state);
  drawAnimatedPair(ctx, metrics, state);
  drawEraseTextBursts(ctx, metrics, state.eraseTextAnimations, state.text);
  drawGameOverOverlay(ctx, metrics, state.gameOverAnimation, state.text.gameOver);

  return metrics;
}
